<template>
    <div>
        <label
            :id="labelId"
            class="block text-base font-medium text-black font-sans mb-1"
        >
            {{ label }}
        </label>

        <div class="relative mt-2 text-base">
            <div class="relative">
                <button
                    type="button"
                    class="grid cursor-pointer w-full grid-cols-1 rounded-sm bg-white py-2 pl-4 pr-12 text-left text-black outline outline-1 -outline-offset-1 outline-slate-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary text-base"
                    :aria-expanded="open"
                    :aria-labelledby="labelId"
                    @click="toggle"
                >
                    <span class="col-start-1 row-start-1 truncate">
                        {{ selectedLabel || placeholder }}
                    </span>
                    <div
                        class="absolute right-2 top-2 flex gap-1 flex-row items-center"
                    >
                        <button
                            v-if="modelValue"
                            type="button"
                            @click.stop="clear"
                            class="rounded-full text-xl text-accent font-bold p-0"
                            title="Clear"
                        >
                            <svg
                                class="h-4 w-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </button>

                        <button
                            type="button"
                            class="rounded-full text-xl text-accent font-bold p-0"
                        >
                            <svg
                                class="h-6 w-6 text-slate-500 transition-transform"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.084l3.71-3.854a.75.75 0 111.08 1.04l-4.25 4.41a.75.75 0 01-1.08 0l-4.25-4.41a.75.75 0 01.02-1.06z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </button>
                    </div>
                </button>
            </div>

            <div
                v-if="open"
                class="absolute z-10 mt-1 max-h-60 w-full rounded-md bg-white py-2 text-base shadow-lg ring-1 ring-black/5 focus:outline-none"
            >
                <div
                    class="relative flex items-center justify-between gap-2 px-3 pb-1"
                >
                    <input
                        v-model="query"
                        type="text"
                        placeholder="Search..."
                        class="w-full border-b border-gray-200 py-2 text-base text-black placeholder-gray-400 focus:outline-none"
                        @keydown.enter.prevent="selectFirstOption"
                    />
                </div>
                <ul
                    class="max-h-48 overflow-auto"
                    role="listbox"
                    :aria-labelledby="labelId"
                >
                    <li
                        v-for="([churchUUID, label], i) in filteredOptions"
                        :key="i"
                        class="relative cursor-default select-none py-2 pl-8 pr-4 text-black hover:bg-primary hover:text-white"
                        @click="select(churchUUID)"
                    >
                        <span
                            :class="[
                                'block truncate',
                                modelValue === churchUUID
                                    ? 'font-semibold'
                                    : 'font-normal',
                            ]"
                        >
                            {{ label }}
                        </span>
                        <span
                            v-if="modelValue === churchUUID"
                            class="absolute inset-y-0 left-0 flex items-center pl-1.5 text-primary"
                        >
                            <svg
                                class="size-5"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </span>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

const props = defineProps<{
    label: string;
    placeholder: string;
    options: [string, string][]; // [churchUUID, label]
    modelValue: string;
}>();

const emit = defineEmits(["update:modelValue"]);

const open = ref(false);
const labelId = "church-selector-label";
const query = ref("");

const filteredOptions = computed(() => {
    return props.options.filter(([, label]) =>
        label.toLowerCase().includes(query.value.toLowerCase()),
    );
});

const selectedLabel = computed(() => {
    return props.options.find(([uuid]) => uuid === props.modelValue)?.[1] ?? "";
});

function toggle() {
    open.value = !open.value;
}

function select(value: string) {
    emit("update:modelValue", value);
    open.value = false;
    query.value = "";
}

function selectFirstOption() {
    const first = filteredOptions.value[0];

    if (first) {
        select(first[0]);
    }
}

function clear() {
    emit("update:modelValue", "");
    query.value = "";
    open.value = false;
}
</script>

<style scoped>
ul::-webkit-scrollbar {
    width: 6px;
}

ul::-webkit-scrollbar-thumb {
    background-color: #d1d5db;
    border-radius: 6px;
}
</style>
